import {
  Badge,
  Button,
  Grid2,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { IconButton, Icons, SkeletonText } from 'utoniq-ui'

import {
  CollectionLabel,
  EventCollectionDetailQuery,
  EventCollectionItemsConnectionFragment,
  Maybe,
  StoreItemFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, Header, HeaderImageArea } from '@/components/layouts'
import {
  PickupLabel,
  StoreItemArea,
  StoreItemAreaSkeleton,
} from '@/components/organisms'
import { ScrollFetcher, Tabs } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { App } from '@/core/native'
import { useAnalytics } from '@/lib/analytics'
import { usePrivateAction } from '@/lib/privateAction'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  collection: EventCollectionDetailQuery['collection'] | null
  userCartCount: number
  eventId: string
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onChangeLabel: (isFirstLabel: boolean, name: string) => Promise<void>
  onFetchMore: (isFirstLabel: boolean, label?: string) => Promise<void>
  onClickShare: () => Promise<void>
}

const defaultLabels: Maybe<Pick<CollectionLabel, 'id' | 'name'>>[] = [null]

export const EventCollectionDetailTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onClickShare,
  onChangeLabel,
}) => {
  const { routerNames } = useRouter()
  const [currentTab, setCurrentTab] = useState<number>(0)
  const { analytics } = useAnalytics()
  const thumbnailUri =
    state.collection?.thumbnail.mediumUri ??
    state.collection?.thumbnail.uri ??
    ''

  useEffect(() => {
    if (!state.collection) {
      return
    }

    analytics?.track('view_item_list', {
      item_list_id: 'event_store_list',
      item_list_name: 'Event Store Items',
      items: state.collection?.collectionItems?.edges?.map(({ node }) => ({
        item_id: node.item.id,
        item_name: node.item.title,
        item_category: 'store_item',
        item_category2: node.item.type,
        item_category3: App.getPlatform(),
        item_category4: 1,
        item_category5: state.collection?.artistId,
        price: node.item.price,
      })),
    })
  }, [useAnalytics, state.collection?.id])

  const handleChangeTab = useCallback(
    async (number: number) => {
      if (!state.collection?.labels?.length) {
        return
      }
      setCurrentTab(number)
      await onChangeLabel(
        number === 0,
        state.collection.labels[number]?.name ?? ''
      )
    },
    [onChangeLabel, state.collection?.labels]
  )

  return (
    <ArtistThemeProvider artistId={state.collection?.artistId}>
      <Head title={routerNames.ShokufesEventCollectionDetail} />
      <BaseLayout>
        <CollectionImageCell
          userCartCount={state.userCartCount}
          loading={state.loading}
          imageUrl={thumbnailUri}
          eventId={state.eventId}
        />
        <CollectionDetailCell
          loading={state.loading}
          collection={state.collection}
          onClickShare={onClickShare}
        />
        <Tabs
          labels={
            state.collection?.labels?.map(label => label?.name ?? '') ?? []
          }
          hiddenLabel={!state.collection?.labels?.length}
          value={currentTab}
          disableAnimateTransitions
          onChange={handleChangeTab}
        >
          {(state.collection?.labels || defaultLabels).map((label, index) => (
            <StoreCollectionListCell
              key={index}
              loading={state.loading}
              hasNextPage={state.hasNextPage}
              collectionItems={state.collection?.collectionItems}
              onFetchMore={() => onFetchMore(index === 0, label?.name)}
              artistId={state.collection?.artist.id ?? ''}
              collectionId={state.collection?.id ?? ''}
              eventId={state.eventId}
            />
          ))}
        </Tabs>
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type CollectionImageCellProps = {
  loading: State['loading']
  imageUrl?: string | null
  userCartCount: number
  eventId: string
}
const CollectionImageCell: Cell<CollectionImageCellProps> = ({
  loading,
  imageUrl,
  userCartCount,
  eventId,
}) => {
  const { handleLoginModal } = usePrivateAction()
  const { router, routerPages, generatePath } = useRouter()

  if (loading && !imageUrl) {
    return (
      <CellFrame>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={212}
          sx={{
            pt: styles.safeAreaTop,
            '& > *': { visibility: 'visible' },
          }}
        >
          <Header
            color="transparent"
            elevation={0}
            sx={{ backgroundColor: 'transparent' }}
            left={
              <Stack
                sx={{
                  borderRadius: '50%',
                  width: 44,
                  height: 44,
                  bgcolor: 'grey.50',
                }}
              />
            }
            right={
              <Stack
                sx={{
                  borderRadius: '50%',
                  width: 44,
                  height: 44,
                  bgcolor: 'grey.50',
                }}
              />
            }
          />
        </Skeleton>
      </CellFrame>
    )
  }
  return (
    <CellFrame sx={{ position: 'relative' }}>
      <Header
        elevation={0}
        position="absolute"
        color="transparent"
        sx={{
          right: 'auto',
          maxWidth: 600,
          bgcolor: 'transparent',
          pt: styles.safeAreaTop,
        }}
        left={
          <IconButton.Close
            edge="start"
            bgcolor="grey.50"
            onClick={() =>
              router.goBack(
                generatePath(routerPages.ShokufesEventDetail, {
                  eventId,
                })
              )
            }
          />
        }
        right={
          <Badge
            badgeContent={userCartCount}
            color="primary"
            sx={{
              '& > span': ({ typography }) => ({
                top: 8,
                minWidth: 16,
                width: 16,
                height: 16,
                ...typography.overline,
                color: 'white',
              }),
            }}
          >
            <IconButton.Store
              edge="end"
              bgcolor="grey.50"
              size="medium"
              onClick={handleLoginModal(
                () => router.push(routerPages.StoreCartList),
                true
              )}
            />
          </Badge>
        }
      />
      <HeaderImageArea
        imageUri={imageUrl}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
      />
    </CellFrame>
  )
}

type CollectionDetailCellProps = {
  loading: State['loading']
  collection?: State['collection']
  onClickShare: Action['onClickShare']
}
const CollectionDetailCell: Cell<CollectionDetailCellProps> = ({
  loading,
  collection,
  onClickShare,
}) => {
  const { t } = useTranslation('eventCollectionDetail')
  if (loading && !collection) {
    return (
      <CellFrame sx={{ px: 5, py: 5 }}>
        <Stack direction="row" sx={{ pb: 1 }}>
          <Stack flex={1} spacing={2}>
            <SkeletonText variant="large" width="20%" />
            <SkeletonText variant="large" width="50%" />
            <SkeletonText variant="small" width="30%" />
          </Stack>
          <Skeleton variant="rectangular" width={38} height={44} />
        </Stack>
        <Stack spacing={2}>
          <SkeletonText variant="body2" numberOfLine={3} lastWidth="60%" />
        </Stack>
      </CellFrame>
    )
  }
  return (
    <CellFrame sx={{ px: 5, py: 5 }}>
      <Stack direction="row">
        <Stack flex={1} spacing={1}>
          <PickupLabel isEventPickup={!!collection?.isEventPickup} />
          <Typography variant="subtitle2" color="text.secondary">
            {collection?.artist.displayName}
          </Typography>
          <Typography variant="h3">{collection?.title}</Typography>
          <Typography variant="caption">
            {t('itemNumber', { number: collection?.itemCount })}
          </Typography>
        </Stack>
        <Stack>
          <Button
            variant="text"
            sx={{ px: 0, display: 'grid', gridTemplateColumns: '1fr' }}
          >
            <Icons.Share
              fontSize="small"
              sx={{ color: 'grey.600', mx: 'auto' }}
              onClick={onClickShare}
            />
            <Typography variant="overline" color="text.hint">
              {t('share')}
            </Typography>
          </Button>
        </Stack>
      </Stack>
      <Typography variant="body2">{collection?.description}</Typography>
    </CellFrame>
  )
}

type StoreCollectionListCellProps = {
  loading: State['loading']
  hasNextPage: State['hasNextPage']
  onFetchMore: () => Promise<void>
  collectionItems?: EventCollectionItemsConnectionFragment
  artistId: string
  collectionId: string
  eventId: string
}
const StoreCollectionListCell: Cell<StoreCollectionListCellProps> = ({
  loading,
  hasNextPage,
  collectionItems,
  onFetchMore,
  artistId,
  collectionId,
  eventId,
}) => {
  const { t } = useTranslation('eventCollectionDetail')
  const { analytics } = useAnalytics()

  return (
    <CellFrame sx={{ px: 5, pb: 5 }}>
      {loading && !collectionItems?.edges ? (
        <Stack sx={{ py: 2 }}>
          <SkeletonText variant="h3" width="50%" />
        </Stack>
      ) : (
        <Typography variant="h3" sx={{ py: 2 }}>
          {t('productInfo')}
        </Typography>
      )}
      <ScrollFetcher
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        component={Grid2}
        componentProps={{ spacing: 3, container: true }}
        itemComponent={Grid2}
        itemComponentProps={{ size: { xs: 4, md: 3 } }}
        nodes={collectionItems?.edges?.map(({ node }) => (
          <StoreItemArea
            canGoBack
            key={node.id}
            collectionId={collectionId}
            item={node.item as StoreItemFragment}
            artistId={artistId}
            eventId={eventId}
            onClick={() => {
              analytics?.track('select_item', {
                item_list_id: `event_store_list_${artistId}`,
                item_list_name: 'Event Store Items',
                items: [
                  {
                    item_id: node.item.id,
                    item_name: node.item.title,
                    item_category: 'store_item',
                    item_category2: node.item.type,
                    item_category3: App.getPlatform(),
                    item_category4: 1,
                    item_category5: artistId,
                    price: node.item.price,
                  },
                ],
              })
            }}
          />
        ))}
        emptyMessage={t('productInfoEmptyMessage')}
        loadingComponent={
          <Grid2 container spacing={3} sx={{ py: 2 }}>
            {Array.from(Array(6).keys()).map(key => (
              <Grid2 size={{ xs: 4, md: 3 }} key={key}>
                <StoreItemAreaSkeleton />
              </Grid2>
            ))}
          </Grid2>
        }
      />
    </CellFrame>
  )
}
